import { axiosInstance } from "./axios";
import { IDossierFolder } from "dto/Dossier/IDossierFolder";
import { IGetItemsResponse } from "dto/Dossier/IGetItemsResponse";
import { IError } from "dto/IError";
import { IKey } from "dto/IKey";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { useTrashConfirmModalStore } from "store/trashConfirm.store";
import { ListType } from "dto/ListType";
import { DossierItemCategory } from "dto/Dossier/DossierItemCategory";
import { ItemKind } from "dto/IKindItems";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import qs from "qs";

const queryKeys = {
  getFolder: (folderId: string) => ["dossierService.getFolder", folderId],
  getFolders: (caseId: string) => ["dossierService.getFolders", caseId],
  getFolderNames: (searchPattern: string) => [
    "dossierService.getFolderNames",
    searchPattern,
  ],
  getItems: (payload: IGetItemsRequest) => ["dossierService.getItems", payload],
};

export interface IMoveDosier {
  Id: string;
  Order: number;
  ParentId: string | null;
}

export interface IGetItemsRequest {
  FilterId?: string;
  CaseId?: string;
  FolderId?: string;
  Text?: string | null;
  IncludeFolders?: boolean;
  IncludeShared?: boolean;
  Shared?: boolean;
  SharedFromUserId?: string;
  SharedToUserId?: string;
  enabled?: boolean;
  ShowInDesk?: boolean;
  Id?: string;
  Kind?: ItemKind;
  Type?: DossierItemType;

  StatusId?: number;
  TagId?: string;

  PaginationModel?: GridPaginationModel;
  SortModel?: GridSortModel;
  FilterModel?: GridFilterModel;
  GroupKeys?: string[];
  GroupFields?: string[];

  Start?: number | string;
  End?: number;
}

const moveDossier = (payload: IMoveDosier): Promise<{ data: IError }> => {
  return axiosInstance.post("/dossier/move", payload);
};

const useMoveDossier = () => {
  return useCustomMutation(moveDossier);
};

export interface ICreateFolder {
  CaseId: string;
  ParentId?: string;
  Name: string;
}

const createFolder = (
  payload: ICreateFolder
): Promise<{ data: IDossierFolder }> => {
  return axiosInstance.post("/dossier/folder", payload);
};

const useCreateFolder = () => {
  return useCustomMutation(createFolder);
};

export interface IUpdateLabel {
  Id: string;
  Label: string;
}

const updateLabel = async (
  payload: IUpdateLabel
): Promise<{ data: IDossierFolder }> => {
  return await axiosInstance.put("/dossier/label", payload);
};

const useUpdateLabel = () => {
  return useCustomMutation(updateLabel);
};

export interface ICreateVersion {
  Id: string;
  Name: string;
}

const createVersion = async (
  payload: ICreateVersion
): Promise<{ data: IError }> => {
  return await axiosInstance.post("/dossier/version", payload);
};

const useCreateVersion = () => {
  return useCustomMutation(createVersion);
};

export interface IRemoveVersion {
  Id: string;
  VersionId: string;
}

const removeVersion = async (
  payload: IRemoveVersion
): Promise<{ data: IError }> => {
  return await axiosInstance.delete("/dossier/version", { data: payload });
};

const useRemoveVersion = () => {
  return useCustomMutation(removeVersion);
};

export interface IDeleteDossier {
  Id: string;
}

const deleteDossier = (payload: IDeleteDossier): Promise<{ data: IError }> => {
  return axiosInstance.delete("/dossier", {
    data: payload,
  });
};

const useDeleteDossier = () => {
  return useCustomMutation(deleteDossier, {
    // Po przeniesieniu pliku Dysku do kosza pokaż globalny modal potwierdzający
    // (chyba że użytkownik wybrał „Nie pokazuj ponownie").
    onSuccess: () => useTrashConfirmModalStore.getState().open(),
  });
};

// Przywrócenie usuniętego elementu Dysku z kosza.
const restoreDossier = (payload: IDeleteDossier): Promise<{ data: IError }> => {
  return axiosInstance.post("/dossier/restore", payload);
};

const useRestoreDossier = () => {
  return useCustomMutation(restoreDossier);
};

export interface IDossierFile {
  KeyId: string;
  Uri: string;
  tagIds: string[];
  Keys: IKey[];
  ObjectKeys: IKey[];
}
export interface IDossierAddFile {
  CaseId?: string;
  Label?: string;
  Files: IDossierFile[];
  ParentId?: string;
  SourceId?: string;
  Replace?: boolean;
  TagIds?: string[];
}

const addFile = (
  payload: IDossierAddFile
): Promise<{ data: IGetItemsResponse }> => {
  return axiosInstance.post("/dossier/file", payload);
};

const useAddFile = () => {
  return useCustomMutation(addFile);
};

export interface ISetDesk {
  Items: string[];
  Show: boolean;
}

const setDesk = (payload: ISetDesk): Promise<{ data: IError }> => {
  return axiosInstance.post("/dossier/desk", payload);
};

const useSetDesk = () => {
  return useCustomMutation(setDesk);
};

export interface ISetTemplate {
  Items: string[];
  IsTemplate: boolean;
}

const setTemplate = (payload: ISetTemplate): Promise<{ data: IError }> => {
  return axiosInstance.post("/dossier/template", payload);
};

const useSetTemplate = () => {
  return useCustomMutation(setTemplate);
};

export interface IDossierAddLink {
  CaseId?: string;
  ParentId?: string;
  Label: string;
  Url: string;
  TagIds?: string[];
}

const addLink = (payload: IDossierAddLink): Promise<{ data: IDossierFile }> => {
  return axiosInstance.post("/dossier/link", payload);
};

const useAddLink = () => {
  return useCustomMutation(addLink);
};

export interface IUpdateDossierFlag {
  Add: number | null;
  Id: string;
  Remove: number | null;
}

const setFlags = (payload: IUpdateDossierFlag) => {
  return axiosInstance.put("/dossier/flags", payload);
};

const useSetFlags = () => {
  return useCustomMutation(setFlags);
};

const getFolder = async (folderId: string): Promise<IDossierFolder> => {
  const r = await axiosInstance.get(`/dossier/folder?id=${folderId}`, {});
  return r.data ?? [];
};

const useGetFolder = (folderId: string, enabled: boolean) => {
  return useCustomQuery(
    queryKeys.getFolder(folderId),
    () => getFolder(folderId),
    {
      enabled: enabled,
    }
  );
  ("");
};

const getFolders = async (
  caseId: string
): Promise<{ data: IDossierFolder[] }> => {
  const r = await axiosInstance.get(`/dossier/folders?id=${caseId}`, {});
  return r.data ?? [];
};

const useGetFolders = (caseId: string, enabled: boolean) => {
  return useCustomQuery(
    queryKeys.getFolders(caseId),
    () => getFolders(caseId),
    {
      enabled: enabled,
    }
  );
  ("");
};

const getFolderNames = async (searchPattern?: string): Promise<string[]> => {
  const r = await axiosInstance.get(
    `/dossier/folder/names?search=${searchPattern}`,
    {}
  );
  return r.data ?? [];
};

const useGetFolderNames = (searchPattern: string, enabled: boolean) => {
  return useCustomQuery(
    queryKeys.getFolderNames(searchPattern),
    () => getFolderNames(searchPattern),
    {
      enabled: enabled,
    }
  );
  ("");
};

export interface ISign {
  Id: string;
  Uri?: string;
  XadesUri?: string;
  Report?: string;
  Countersign?: boolean;
}

const sign = (payload: ISign): Promise<{ data: IError }> => {
  return axiosInstance.post("/dossier/sign", payload);
};

const useSign = () => {
  return useCustomMutation(sign);
};

const getItems = async (
  request: IGetItemsRequest
): Promise<{ data: IGetItemsResponse }> => {
  const queryString = qs.stringify(request, {
    allowDots: true,
    skipNulls: true,
  });

  return axiosInstance.get(`/dossier?${queryString}`);
};

const useGetItems = (payload: IGetItemsRequest) => {
  return useCustomQuery(queryKeys.getItems(payload), () => getItems(payload), {
    keepPreviousData: false,
    enabled: payload.enabled,
  });
};

const useMutationGetItems = () => {
  return useCustomMutation(getItems);
};

export interface IGetFiltersRequest {
  ListType: ListType;
}

const getFilters = (request: IGetFiltersRequest) => {
  const query = new URLSearchParams(window.location.search);

  query.append("ListType", request.ListType.valueOf().toString());
  return axiosInstance.get(`/dossier/filters?${query}`);
};

const useGetFilters = () => {
  return useCustomMutation(getFilters);
};

export interface ICreateFilterRequest {
  Id?: string;
  ListType: ListType;

  Name: string;
  Category: DossierItemCategory;
  SharedToUserId?: string;
  SharedByUserId?: string;
  Shared?: boolean;
  TagIds?: string[];
  StatusIds?: number[];
}

const createFilter = (payload: ICreateFilterRequest) => {
  return axiosInstance.post("/dossier/filter", payload);
};

const useCreateFilter = () => {
  return useCustomMutation(createFilter);
};

// Opróżnienie kosza Dysku — trwałe usunięcie usuniętych plików bieżącego profilu.
const emptyTrash = () => {
  return axiosInstance.post("/dossier/trash/empty", {});
};

const useEmptyTrash = () => {
  return useCustomMutation(emptyTrash);
};

export default {
  useMoveDossier,
  useCreateFolder,
  useUpdateLabel,
  useDeleteDossier,
  useRestoreDossier,
  useAddFile,
  useAddLink,
  useSetFlags,
  useGetFolder,
  useGetFolders,
  useSign,
  useCreateVersion,
  useRemoveVersion,
  getItems,
  useGetItems,
  useMutationGetItems,
  useGetFilters,
  useCreateFilter,
  useEmptyTrash,
  useGetFolderNames,
  useSetDesk,
  useSetTemplate,
};
